import { digger } from '@cbre/digger';
import {
  concatValuesBy,
  defaultTxt,
  formatNumber,
  genThumbMediaURI,
  getImageByType,
  getPrimarySecureImage,
  isMediaImage,
  isMediaInfoPublishable,
  SecureImage,
} from '@miq/utils';
import { Observable } from 'rxjs';
import { LocaleId } from '../../../models/locale.model';
import { ReportRecord } from '../../../models/report.model';
import { DisplayFnArgumentsInfo, DisplayFunctionOptions, LovValues } from '../../../models/template.model';
import { getMediaInformation } from '../templates-formatter.const';
import { mapAndFormatDynamicValue } from '../utils/dynamic-data-retrievers';

// ****************** STATIC FIELD IDs ********************
/*
 * NOTE: Whatever new ID is added, we need to make sure that the backend has
 * created the ID on the DB.
 */
export const IMAGE_FIELD_ID = 98;
export const ADDITIONAL_IMAGE_FIELD_ID = 99;
export const LAYOUT_IMAGE_3_ID = 254;
export const IMAGE_STATIC = 124;
export const CONFIDENTIALITY_FIELD_ID = 172;
export const LISTING_BROKER_ID = 309;
export const AVAILABILITY_FLOOR_PLAN = 347;
export const TOTAL_SIZE_FIELD_ID = {
  property: 329,
  lease: 332,
  tim: 221,
  sale: 330,
  availability: 331,
};
export const FLOORPLAN_FIELD_ID = [263, 876];
export const NON_EDITABLE_FIELD_IDS = [317, 318];
// ********************************************************

// **************** STATIC FIELD STRINGS ******************
const yieldField = 'arrangement_yield_detail';
// ********************************************************

// Entities
export enum Entities {
  'Tim' = 'tim',
}

export enum AdditionalImagesPosition {
  AdditionalImage = 0,
  SecondaryImage = 1,
}

export enum ExternalRatingFieldsDesc {
  leed = 'LEED',
  green = 'Green',
}

export function getSecureImage(
  record: ReportRecord,
  settings: {
    urlSplitter: string;
    urlPrefix: string;
    onlyUrl: boolean;
    isPDF?: boolean;
  }
): string | SecureImage {
  // TODO(ppt): check how to deal with arrays instead of picking only the first one.
  // For every entity Report need to show the property Primary Image.
  // Example: In china Availability Report, this method is show availability report. So change to get the property primary image.
  let recordData;
  if (record.associatedProperty && record.associatedProperty.getMediaInformation) {
    recordData = record.associatedProperty;
  } else {
    recordData = Array.isArray(record.data) ? record.data[0] : record.data;
  }
  const primaryMediaInfoArr = recordData.getMediaInformation();
  const alternativeMediaInfoArr = getMediaInformation(record);

  return getPrimarySecureImage(primaryMediaInfoArr, alternativeMediaInfoArr, settings);
}

export function getFloorPlanImage(
  record: ReportRecord,
  settings: {
    urlSplitter: string;
    urlPrefix: string;
    onlyUrl: boolean;
    isPDF?: boolean;
    provideDefault?: boolean;
  }
): string | SecureImage {
  const { urlSplitter, urlPrefix, provideDefault = true, onlyUrl = true, isPDF = false } = settings;
  const rawRecordMedia =
    record.associatedProperty != null
      ? record.associatedProperty.getMediaInformation()
      : record.data.getMediaInformation();
  const recordMedia = (rawRecordMedia || []).filter(media => isMediaImage(media));
  const floorPlanMedia = recordMedia != null ? getImageByType(recordMedia) : null;
  const floorPlanMediaPath = floorPlanMedia != null ? floorPlanMedia.media_path : null;
  const result: SecureImage = {
    imageSrc: genThumbMediaURI(urlSplitter, urlPrefix, floorPlanMediaPath, {
      provideDefault,
    }),
    publishable: isMediaInfoPublishable(recordMedia),
    isPDF,
  };
  return onlyUrl ? (result.imageSrc as string) : result;
}

export enum AvailabilityTypesLabels {
  'Lease' = 'FOR LEASE',
  'Sale' = 'FOR SALE',
  'Sale or Lease' = 'FOR SALE AND LEASE',
}

export const AVAILABILITY_TYPES = {
  lease: 'Lease',
  sale: 'Sale',
  investmentSale: 'Investment Sale',
  userSale: 'User Sale',
  saleOrLease: 'Sale or Lease',
  unknown: 'Unknown',
};

export const USAGE_AMENITY_FIELD = 'usage_amenity/property_amenity_type_desc';

export const YIELD_DETAILS = {
  quoteInitialYield: 'Quoting Price Net Initial Yield',
  equivalentYield: 'Equivalent Yield',
  reversionaryYield: 'Reversionary Yield',
  grossInitialYield: 'Gross Initial Yield',
  initialYield: 'Initial Yield',
};

export function submarketLov(options: DisplayFunctionOptions): Observable<LovValues[]> {
  const { locale, reportExportViewService } = options;
  const uri = `/geo/submarkets/uniques?locale=${locale}`;
  return reportExportViewService.getLovValues(uri);
}

export const getYieldPercentageByYieldType = (record: ReportRecord, yieldType: string): string => {
  const yieldRecords = digger(record.data, yieldField);
  let yieldPercentage = defaultTxt;
  if (yieldRecords && yieldRecords.length) {
    const yieldPercentRecords = yieldRecords.filter(quote => quote.yield_type_desc === yieldType);
    if (yieldPercentRecords && yieldPercentRecords.length) {
      yieldPercentage = yieldPercentRecords
        .map(({ yield_percent }) => formatNumber(yield_percent, undefined, '.2-2'))
        .join(', ');
    }
  }
  return yieldPercentage;
};

export function getDisclaimerByLocale(localeId: LocaleId): string {
  let disclaimer: string;
  switch (localeId) {
    case LocaleId.ItalyEnglish:
      disclaimer =
        'These particulars are issued by CBRE Italy on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE Italy for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Italy, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.';
      break;
    case LocaleId.ItalyItalian:
      disclaimer =
        'Questi dati sono pubblicati da CBRE a condizione che qualunque negoziazione relativa alla proprietà sia condotta attraverso CBRE. Mentre è stata prestata ogni attenzione nella preparazione degli stessi, CBRE in nome proprio e dei suoi agenti fa presente che: -(i) i dati sono esposti come indicazioni di massima per potenziali acquirenti/locatori e non costituiscono parte di un’offerta o contratto; (ii) qualsiasi rappresentazione incluse le descrizioni, le dimensioni, i riferimenti allo stato dell’immobile, a permessi o licenze per usi o occupazioni, accessi e qualunque altro dettaglio viene fornita in buona fede ed è ritenuta corretta, ma qualsiasi potenziale acquirente o locatore dovrebbe non fare affidamento su tali rappresentazioni, ma verificare le stesse (a proprie cure e spese) in merito alla loro correttezza; (iii) né CBRE, né alcuno dei suoi dipendenti ha alcuna autorità per fornire dichiarazioni o garanzie in relazione all’immobile.';
      break;
    default:
      disclaimer = '';
  }
  return disclaimer;
}

export function getFloorAndSuite() {
  return {
    553: {
      // Floor/Suite
      displayFn: (record: ReportRecord, _, suite: DisplayFnArgumentsInfo, floorNumber: DisplayFnArgumentsInfo) => {
        let suiteVal = mapAndFormatDynamicValue(record, suite, null, null, false);
        let floorNumberVal = mapAndFormatDynamicValue(record, floorNumber, null, null, false);
        suiteVal = Array.isArray(suiteVal) ? suiteVal : [suiteVal];
        floorNumberVal = Array.isArray(floorNumberVal) ? floorNumberVal : [floorNumberVal];
        return (suiteVal as string[]).map((val, i) => 'Floor ' + concatValuesBy(' | Suite ', floorNumberVal[i], val));
      },
    },
  };
}

export function getFloorPlanImageSlide() {
  return {
    347: {
      // Floor plan Image
      displayFn: (record: ReportRecord) => {
        return getMediaInformation(record);
      },
    },
  };
}

export function getFloor(record: ReportRecord, index: number) {
  return record.data[index] ? record.data[index].floor.toString() : ' ';
}

export function getTenants(record: ReportRecord, index: number) {
  return record.data[index] && record.data[index].tenants.length > 0 ? record.data[index].tenants.join('; ') : ' ';
}

export function getSelectedAvailability(record: ReportRecord, index: number) {
  if (record.data[index]) {
    const floorSuite = digger(record, 'floor_suite');
    const availableSpace = digger(record, 'available_space');
    const availableSpaceUom = digger(record, 'available_space_uom_desc');
    return record.data[index].floor === Math.floor(floorSuite.floor_number)
      ? fetchValue(floorSuite, availableSpace, availableSpaceUom)
      : ' ';
  } else {
    return ' ';
  }
}

function fetchValue(floorSuite, availableSpace, availableSpaceUom) {
  return concatValuesBy(' ', 'Suite', floorSuite.suite_number, 'Availability', availableSpace, availableSpaceUom);
}
